import type { ErrorInfo, Ref } from 'react';
import type { IHighlight } from 'react-pdf-highlighter';
import { PdfViewer } from './PdfViewer';
import { AppProviders } from './AppProviders';
import { SlotErrorBoundary } from '@libertify/ui';
import { KNOWLEDGE_HUB_THEME } from '../knowledgeHubTheme';
import { useStableDocumentUrl } from '../hooks/useStableDocumentUrl';
import type {
  Cuepoint,
  CustomTheme,
  PublicationIdentifiers,
  TextSelectionHandler,
} from '../types';

const noopT = (key: string) => key;
const noopReset = () => undefined;

/** Imperative handle the PDF viewer exposes for page-level coordination. */
export interface PdfViewerHandle {
  /** Scroll + center the document on the given highlight. */
  scrollViewerTo: (highlight: IHighlight) => void;
}

export interface PdfDocumentViewerProps {
  /** PDF URL to render. */
  url: string;
  /**
   * Highlights to display over the PDF. Pass `null` (default) to
   * render the document without any annotations.
   */
  highlights?: IHighlight[] | null;
  /**
   * Cuepoints enriching the highlights with rough-notation
   * animations + zoom config. PdfViewer does NOT listen to video time
   * — that coordination lives at the page level.
   */
  cuepoints?: Cuepoint[] | null;
  /**
   * Theme used by the underlying styled-components ThemeProvider.
   * Defaults to the Libertify Knowledge Hub theme — pass to override.
   */
  theme?: CustomTheme;
  /**
   * Publication identifiers passed to `useAnalytics` inside PdfViewer.
   * Enables analytics events to include project/publication/org IDs.
   */
  identifiers?: PublicationIdentifiers;
  /** Number of pages in the document (analytics + UX hints). */
  documentPagesCount?: number | null;
  /** Layout flag — currently always true for our use case. */
  isPrimary?: boolean;
  /** Show creator-mode affordances (admin edit). */
  isCreatorMode?: boolean;
  /**
   * Called when the viewer's subtree throws. This wrapper carries its own
   * `SlotErrorBoundary`, which therefore catches *before* the one
   * `ExperiencePageLayout` puts around the document slot — so without this
   * the document panel would be the one slot whose crashes never reach the
   * app's error reporter.
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Ref to the viewer's imperative handle (e.g. `scrollViewerTo` for the
   * video↔document sync). Passed as a normal prop rather than React `ref`
   * so it survives `next/dynamic`, which doesn't forward refs.
   */
  viewerRef?: Ref<PdfViewerHandle>;
  /**
   * Enables switching the displayed document at runtime (re-detect readiness
   * on `url` change + retry `scrollViewerTo` until the new page loads). On by
   * default for this v2 wrapper; the legacy viewer uses `PdfViewer` directly
   * and never sets it, so legacy behavior is unchanged.
   */
  enableDocumentSwitching?: boolean;
  /**
   * Called with each single-page text selection as a backend `SourceZone`,
   * plus a `clearSelection` to drop the browser's selection highlight. Area
   * and cross-page selections are ignored. Unset → selection does nothing.
   */
  onTextSelection?: TextSelectionHandler;
}

/**
 * PdfDocumentViewer — standalone wrapper around the experience-viewer
 * `<PdfViewer>` component. Encapsulates the `<AppProviders>` setup
 * (theme + analytics context) so consumers can drop it into any page
 * without touching experience-viewer's internal context surface.
 *
 * Designed for the new Knowledge Hub experience page in both admin and
 * sales. The full `<ExperienceViewer>` orchestrator is too heavy when
 * we only need the document area; this is the surgical alternative.
 *
 * Exposes PdfViewer's imperative handle via the `viewerRef` prop so the
 * page can call e.g. `scrollViewerTo` for the video↔document sync.
 */
export const PdfDocumentViewer = ({
  url,
  highlights = null,
  cuepoints = null,
  theme = KNOWLEDGE_HUB_THEME,
  identifiers,
  documentPagesCount,
  isPrimary = true,
  isCreatorMode = false,
  viewerRef,
  enableDocumentSwitching = true,
  onError,
  onTextSelection,
}: PdfDocumentViewerProps) => {
  // The publication refetches on every WS project event while add-ons
  // generate, re-signing this url (fresh Expires/Signature) each time —
  // stabilize it so PdfLoader doesn't treat that as a document swap and
  // reload the whole PDF every couple seconds (MED-6986). If the held url
  // fails to load (e.g. a 403 while the document is still generating),
  // reportLoadError un-holds it so the next re-signed url is picked up
  // right away instead of sitting blank for the rest of the hold window.
  const [rawStableUrl, reportLoadError] = useStableDocumentUrl(url);
  const stableUrl = rawStableUrl ?? url;

  return (
    <AppProviders
      theme={theme}
      appContextValue={{
        t: noopT,
        identifiers: identifiers ?? {},
      }}
      onReset={noopReset}
      // This viewer occupies one panel of the experience page, so a crash
      // belongs in that panel. The SlotErrorBoundary below shows the same
      // compact error card the page already uses when a document's *data*
      // fails, instead of a full-page error Result inside a 65%-wide pane.
      // Kept local rather than leaning on the layout's own slot boundary,
      // so this stays contained when rendered outside ExperiencePageLayout.
      withErrorBoundary={false}
    >
      <SlotErrorBoundary
        onError={onError}
        // A document swap changes `url` in place rather than re-mounting
        // (see `enableDocumentSwitching`), so without this the pane would
        // keep showing the previous document's error card.
        resetKey={stableUrl}
      >
        {/* Positioned, bounded, clipped host. PdfViewer's internals
            (react-pdf-highlighter's absolutely-positioned scroll
            container + the absolute zoom controls) anchor to the
            nearest positioned ancestor — without this they escape to
            the viewport and render at full width, spilling over
            neighbouring layout (e.g. the Knowledge Hub sidebar). */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <PdfViewer
            ref={viewerRef}
            url={stableUrl}
            onLoadError={reportLoadError}
            highlights={highlights}
            cuepoints={cuepoints}
            isPrimary={isPrimary}
            documentPagesCount={documentPagesCount}
            isCreatorMode={isCreatorMode}
            enableDocumentSwitching={enableDocumentSwitching}
            onTextSelection={onTextSelection}
          />
        </div>
      </SlotErrorBoundary>
    </AppProviders>
  );
};
